import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { getSignedMediaUrl, formatTimeWithNextDay } from '@/lib/utils';
import { formatCurrencyForDisplay } from '@/lib/utils/currencyFormat';
import type { FloorPlanResponse, FloorPlanItemResponse } from '@/features/booking/types/floorPlanTypes';

interface MobileTablePreviewCardProps {
  floorPlan: FloorPlanResponse | null;
  selectedTable: string | null;
  floorPlanItems: FloorPlanItemResponse[];
  pricePerGuest: number;
  currency?: string;
  categoryImageId?: string | null;
  selectedTimeSlot?: string | null;
  tableSize?: number | null;
  itemType?: string;
  onAddClick: () => void;
  guestCount: number; // to check if it's already added
  eventStartTime?: string | null;
  businessTimeZone?: string;
}

const MobileTablePreviewCard: React.FC<MobileTablePreviewCardProps> = ({
  floorPlan,
  selectedTable,
  floorPlanItems,
  pricePerGuest,
  currency = 'USD',
  categoryImageId,
  selectedTimeSlot,
  businessTimeZone,
  tableSize,
  itemType,
  onAddClick,
  guestCount,
  eventStartTime
}) => {
  const selectedItem = selectedTable
    ? floorPlanItems.find((item) => item.table_number === selectedTable)
    : null;

  // Fallback chain: item image → category image → gradient
  const activeImageId = selectedItem?.image_id || categoryImageId || null;
  const imageUrl = activeImageId
    ? getSignedMediaUrl(activeImageId, 'image').url
    : null;

  const activeTableSize = selectedItem?.table_size || tableSize || 0;

  const totalPrice = activeTableSize
    ? pricePerGuest * activeTableSize
    : 0;

  const formatPrice = (amount: number) => formatCurrencyForDisplay(amount, currency);

  const isAdded = guestCount > 0;

  return (
    <div className="relative w-full aspect-[4/3] overflow-hidden bg-[#0a0a0a] shrink-0 rounded-t-[32px]">
      {/* Image or Fallback */}
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={floorPlan?.name || 'Floor Plan'}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-800 via-gray-900 to-gray-950 flex flex-col items-center justify-center gap-3">
          <svg className="w-10 h-10 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l5.447 2.724A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          <span className="text-gray-500 text-sm font-medium tracking-wide">
            {floorPlan?.name || 'Floor Plan'}
          </span>
        </div>
      )}

      {/* Gradient overlay for seamless blend with popup background */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-black/20 pointer-events-none" />

      {/* Info Overlay */}
      <AnimatePresence mode="wait">
        {selectedTable && (
          <motion.div
            key={selectedTable}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="absolute bottom-0 left-0 right-0 p-5 pointer-events-auto"
          >
            <div className="flex items-end justify-between gap-4">
              <div className="min-w-0 flex flex-col items-start gap-1">
                {/* Item Name */}
                <h4 className="text-white font-black text-2xl tracking-tight leading-none truncate pr-2 max-w-[220px]">
                  {selectedItem?.name || `${itemType || 'Table'} ${selectedItem?.table_number || selectedTable}`}
                </h4>

                <div className="flex flex-col gap-1 mt-1.5 opacity-90">
                  {/* Table Number if name is different */}
                  {selectedItem?.name && selectedItem.name !== selectedItem.table_number && (
                    <div className="text-gray-300 text-sm font-medium">
                      {itemType || 'Table'} {selectedItem.table_number}
                    </div>
                  )}

                  <div className="flex items-center gap-3 text-sm font-medium text-gray-300 flex-wrap">
                    {/* Capacity */}
                    {activeTableSize > 0 && (
                      <div className="flex items-center gap-1.5 whitespace-nowrap">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                        <span>{activeTableSize} {activeTableSize === 1 ? 'seat' : 'seats'}</span>
                      </div>
                    )}

                    {/* Selected Timeslot */}
                    {selectedTimeSlot && (
                      <div className="flex items-center gap-1.5 border-l border-white/20 pl-3 whitespace-nowrap">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <span>
                          {(() => {
                            const timeStr = formatTimeWithNextDay(
                              selectedTimeSlot,
                              eventStartTime || '',
                              businessTimeZone
                            );
                            const baseTime = timeStr.replace('+1', '');
                            
                            // Get exactly date: e.g., 10th March
                            const sltDate = new Date(selectedTimeSlot);
                            const day = sltDate.getDate();
                            const suffix = ['th', 'st', 'nd', 'rd'][(day % 10 > 3) ? 0 : (day % 100 - day % 10 !== 10) ? day % 10 : 0];
                            const month = sltDate.toLocaleDateString('en-US', { month: 'short', timeZone: businessTimeZone });
                            const dateStr = `${day}${suffix} ${month}`;
                            
                            return (
                              <>
                                {dateStr} {baseTime}
                              </>
                            );
                          })()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Side: Price & Add Button */}
              <div className="text-right shrink-0 flex flex-col items-end gap-3 pb-1">
                <div className="flex flex-col items-end">
                  <div className="text-white font-black text-2xl tracking-tight leading-none">
                    {formatPrice(totalPrice)}
                  </div>
                  {activeTableSize > 0 && (
                    <div className="text-gray-400 text-xs mt-1.5 font-medium tracking-wide">
                      {formatPrice(pricePerGuest)} × {activeTableSize}
                    </div>
                  )}
                </div>
                
                <button
                  onClick={onAddClick}
                  className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all shadow-lg active:scale-95 border ${
                    isAdded 
                      ? 'bg-red-500/10 text-red-500 border-red-500/30 hover:bg-red-500/20'
                      : 'bg-blue-600 text-white hover:bg-blue-500 border-blue-500/50 shadow-[0_0_20px_rgba(37,99,235,0.3)]'
                  }`}
                >
                  {isAdded ? 'Remove' : 'Add'}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MobileTablePreviewCard;
